class ProductFormAccordion extends HTMLElement {
  constructor() {
    super();

    this.details = null;
    this.summary = null;
    this.content = null;

    this.isOpen = true;
    this.isAnimating = false;
    this.transitionTimeout = null;
    this.handleTransitionEnd = null;

    this.handleSummaryClick = this.handleSummaryClick.bind(this);
    this.handleOptionChange = this.handleOptionChange.bind(this);
  }

  connectedCallback() {
    this.details = this.querySelector(":scope > .product-form__accordion-details");

    this.summary = this.details?.querySelector(":scope > .product-form__accordion-summary");

    this.content = this.details?.querySelector(":scope > .product-form__accordion-content");

    if (!this.details || !this.summary || !this.content) {
      return;
    }

    this.details.open = true;

    this.isOpen = !this.hasAttribute("closed");

    this.summary.removeEventListener("click", this.handleSummaryClick);

    this.summary.addEventListener("click", this.handleSummaryClick);

    this.removeEventListener("change", this.handleOptionChange);
    this.addEventListener("change", this.handleOptionChange);

    this.setupColorSync();

    this.syncState();
  }

  disconnectedCallback() {
    this.summary?.removeEventListener("click", this.handleSummaryClick);

    this.removeEventListener("change", this.handleOptionChange);

    this.colorSyncObserver?.disconnect();
    this.colorSyncObserver = null;

    this.cleanupTransition();
  }

  setupColorSync() {
    const colorHeaderValue = this.querySelector(
      ":scope > .product-form__accordion-details > .product-form__accordion-summary [data-color-selected-value]",
    );

    const colorPropertyInput = this.querySelector(":scope > [data-color-property-input]");

    const colorPropertyCodeInput = this.querySelector(":scope > [data-color-property-code-input]");

    if (!colorHeaderValue && !colorPropertyInput && !colorPropertyCodeInput) {
      return;
    }

    const syncFromSource = () => {
      const sourceElements = this.querySelectorAll(".product-color-groups__selected-color");

      let selectedText = "";

      sourceElements.forEach((element) => {
        const text = element.textContent.trim();

        if (text) {
          selectedText = text;
        }
      });

      if (!selectedText) {
        return;
      }

      const labelParts = selectedText.split(" - ");
      const colorName = labelParts[0].trim();

      if (colorHeaderValue) {
        colorHeaderValue.textContent = selectedText;
      }

      if (colorPropertyInput) {
        colorPropertyInput.value = selectedText;
      }

      if (colorPropertyCodeInput) {
        const labelParts = selectedText.split(" - ");

        colorPropertyCodeInput.value = labelParts.length > 1 ? labelParts[labelParts.length - 1].trim() : "";
      }
    };

    syncFromSource();

    const contentRoot = this.content || this;

    this.colorSyncObserver = new MutationObserver(syncFromSource);

    this.colorSyncObserver.observe(contentRoot, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });
  }

  handleOptionChange(event) {
    const optionInput = event.target.closest("[data-product-option-input]");

    if (optionInput) {
      const position = optionInput.getAttribute("data-option-position");
      const value = optionInput.getAttribute("data-option-value");

      this.updateSelectedValue("data-option-selected-value", position, value);

      return;
    }

    const propertyInput = event.target.closest("[data-product-property-input]");

    if (propertyInput) {
      const rawValue = propertyInput.getAttribute("data-property-value");

      const displayValue = this.formatDeliveryDisplayValue(rawValue);

      this.updateSelectedValue("data-property-selected-value", null, displayValue);
    }
  }

  formatDeliveryDisplayValue(rawValue) {
    if (!rawValue || rawValue.indexOf("|") === -1) {
      return rawValue;
    }

    const [title, , note] = rawValue.split("|").map((part) => part.trim());

    if (note === "Included") {
      return `${title} (${note})`;
    }

    return title;
  }

  updateSelectedValue(attributeName, position, value) {
    const selector = position ? `[${attributeName}="${position}"]` : `[${attributeName}]`;

    this.querySelectorAll(selector).forEach((element) => {
      element.textContent = value;
    });
  }

  syncState() {
    this.details.open = true;

    this.classList.toggle("is-open", this.isOpen);
    this.classList.toggle("is-closed", !this.isOpen);

    this.details.classList.toggle("open", this.isOpen);
    this.content.classList.toggle("is-open", this.isOpen);

    this.summary.setAttribute("aria-expanded", String(this.isOpen));

    if (this.isOpen) {
      this.content.style.removeProperty("height");
      this.content.style.removeProperty("opacity");
      this.content.style.removeProperty("overflow");
      this.content.style.removeProperty("visibility");
      this.content.removeAttribute("aria-hidden");
    } else {
      this.content.style.height = "0px";
      this.content.style.opacity = "0";
      this.content.style.overflow = "hidden";
      this.content.style.visibility = "hidden";
      this.content.setAttribute("aria-hidden", "true");
    }

    this.isAnimating = false;
  }

  handleSummaryClick(event) {
    if (event.target.closest("a, button, input, select, textarea, label")) {
      return;
    }

    event.preventDefault();

    if (this.isAnimating) {
      return;
    }

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen || this.isAnimating) {
      return;
    }

    this.cleanupTransition();

    this.isOpen = true;
    this.isAnimating = true;

    this.details.open = true;
    this.details.classList.add("open");
    this.content.classList.add("is-open");

    this.classList.add("is-open");
    this.classList.remove("is-closed");

    this.summary.setAttribute("aria-expanded", "true");

    this.content.removeAttribute("aria-hidden");
    this.content.style.visibility = "visible";
    this.content.style.overflow = "hidden";
    this.content.style.height = "0px";
    this.content.style.opacity = "0";

    this.content.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.content.style.height = `${this.content.scrollHeight}px`;
      this.content.style.opacity = "1";
    });

    this.waitForTransition(() => {
      this.content.style.removeProperty("height");
      this.content.style.removeProperty("opacity");
      this.content.style.removeProperty("overflow");
      this.content.style.removeProperty("visibility");

      this.isAnimating = false;
    });
  }

  close() {
    if (!this.isOpen || this.isAnimating) {
      return;
    }

    this.cleanupTransition();

    this.isOpen = false;
    this.isAnimating = true;

    this.content.style.height = `${this.content.scrollHeight}px`;
    this.content.style.opacity = "1";
    this.content.style.overflow = "hidden";
    this.content.style.visibility = "visible";

    this.content.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.details.classList.remove("open");
      this.content.classList.remove("is-open");

      this.classList.remove("is-open");
      this.classList.add("is-closed");

      this.summary.setAttribute("aria-expanded", "false");

      this.content.style.height = "0px";
      this.content.style.opacity = "0";
    });

    this.waitForTransition(() => {
      this.details.open = true;

      this.content.style.height = "0px";
      this.content.style.opacity = "0";
      this.content.style.overflow = "hidden";
      this.content.style.visibility = "hidden";
      this.content.setAttribute("aria-hidden", "true");

      this.isAnimating = false;
    });
  }

  waitForTransition(callback) {
    let completed = false;

    const finish = () => {
      if (completed) {
        return;
      }

      completed = true;

      this.cleanupTransition();
      callback();
    };

    this.handleTransitionEnd = (event) => {
      if (event.target !== this.content || event.propertyName !== "height") {
        return;
      }

      finish();
    };

    this.content.addEventListener("transitionend", this.handleTransitionEnd);

    this.transitionTimeout = window.setTimeout(finish, 400);
  }

  cleanupTransition() {
    if (this.handleTransitionEnd && this.content) {
      this.content.removeEventListener("transitionend", this.handleTransitionEnd);
    }

    if (this.transitionTimeout) {
      window.clearTimeout(this.transitionTimeout);
    }

    this.handleTransitionEnd = null;
    this.transitionTimeout = null;
  }
}

if (!customElements.get("product-form-accordion")) {
  customElements.define("product-form-accordion", ProductFormAccordion);
}

// Own attribute (not [data-add-to-wishlist]): product-card.js stops propagation on that one even
// outside a card, which would swallow this click. Saves straight to Swym, no colour drawer.
const pdpWishlistSelector = "[data-pdp-add-to-wishlist]";

function setPdpWishlistSaved(button, saved) {
  button.setAttribute("aria-pressed", String(saved));

  const label = button.querySelector("[data-wishlist-label]");

  if (label) {
    label.textContent = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
  }
}

// Every Swym call needs its error callback: the SDK invokes it unconditionally on a failed
// response, so omitting it throws inside Swym instead of reporting the failure back here.
function withSwymList(onList, onFailed) {
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push((swat) => {
    swat.fetchLists({
      callbackFn: (lists) => onList(swat, lists?.[0] || null),
      errorFn: onFailed,
    });
  });
}

function syncPdpWishlistButtons() {
  const buttons = document.querySelectorAll(pdpWishlistSelector);

  if (!buttons.length) return;

  withSwymList(
    (swat, list) => {
      const savedIds = new Set((list?.listcontents || []).map((item) => String(item.empi)));

      buttons.forEach((button) => setPdpWishlistSaved(button, savedIds.has(button.dataset.wishlistProductId)));
    },
    () => {},
  );
}

syncPdpWishlistButtons();
document.addEventListener("product:ajax-rendered", syncPdpWishlistButtons);
document.addEventListener("wishlist:updated", (event) => {
  if (event.detail?.source !== "pdp") syncPdpWishlistButtons();
});

document.addEventListener("click", (event) => {
  const button = event.target.closest(pdpWishlistSelector);

  if (!button) return;

  event.preventDefault();

  if (button.disabled) return;

  const item = {
    epi: Number(button.dataset.wishlistVariantId),
    empi: Number(button.dataset.wishlistProductId),
    du: button.dataset.wishlistProductUrl,
    qty: 1,
  };

  if (!item.epi || !item.empi) return;

  const saved = button.getAttribute("aria-pressed") === "true";

  button.disabled = true;

  const onDone = (nowSaved) => {
    button.disabled = false;
    setPdpWishlistSaved(button, nowSaved);
    document.dispatchEvent(new CustomEvent("wishlist:updated", { bubbles: true, detail: { source: "pdp" } }));
    window.showToast(
      nowSaved
        ? window.localeStrings?.wishlist?.added || "Added to wishlist"
        : window.localeStrings?.wishlist?.removed || "Removed from wishlist",
    );
  };

  const onFailed = () => {
    button.disabled = false;
    window.showToast(window.localeStrings?.wishlist?.error || "Could not update wishlist");
  };

  withSwymList((swat, list) => {
    if (saved) {
      if (!list) {
        onFailed();
        return;
      }

      swat.deleteFromList(list.lid, item, () => onDone(false), onFailed);
      return;
    }

    const add = (listId) => swat.addToList(listId, item, () => onDone(true), onFailed);

    if (list) {
      add(list.lid);
      return;
    }

    swat.createList({ lname: "My Wishlist" }, (newList) => add(newList.lid), onFailed);
  }, onFailed);
});
